import React, { useEffect, useState } from 'react';
import { Card } from 'reactstrap';
import * as Icon from 'react-feather';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { useDebouncedCallback } from 'use-debounce';

import AdminHeader from '../../../components/header/AdminHeader';
import CallexLoader from '../../../components/CallexLoader';
import Pagination from '../../../components/form/Pagination';
import DeleteConfirmationModal from '../../../components/Modals/DeleteConfirmationModal';

import AddEditQueueModal from './AddEditQueueModal';
import {
  queuesDelete,
  queuesListGet,
  useQueuesList
} from '../../../store/slices/tenantSlices/incomingSlice';
import { checkModulePermission } from '../../../utils/common';
import { useUserRolePermission } from '../../../store/slices/authSlice';

const Queues = () => {
  const dispatch = useDispatch();
  const queuesList = useQueuesList();
  const userRolePermission = useUserRolePermission();

  const [openAddModal, setOpenAddModal] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [isEdit, setIsEdit] = useState(null);
  const [isDeleteId, setIsDeleteId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [filter, setFilter] = useState({
    page: 1,
    limit: 10,
    search: undefined
  });

  const fetchQueuesList = useDebouncedCallback(async () => {
    setIsLoading(true);
    try {
      const payload = { ...filter };
      await dispatch(queuesListGet(payload)).unwrap();
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log('error', error);
    }
  }, 500);

  const deleteBreak = async () => {
    try {
      const response = await dispatch(queuesDelete(isDeleteId)).unwrap();
      if (response) {
        toast.success('Record deleted successfully');
        if (queuesList?.data?.length === 1) {
          setFilter({ ...filter, page: filter?.page > 1 ? filter.page - 1 : 1 });
        } else {
          fetchQueuesList();
        }
        setIsDeleteId(null);
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    fetchQueuesList();
  }, [filter]);

  return (
    <div>
      <AdminHeader
        title="Queues"
        isExpandSearch
        isAdd
        isAddDisabled={!checkModulePermission('is_add', 'Queues', userRolePermission)}
        onAddClick={() => setOpenAddModal(true)}
        addBtnName="Add Queues"
        searchPlaceholder="Search Queues"
        search={filter.search}
        handelSearch={(e) => setFilter({ ...filter, search: e.target.value })}
      />
      {isLoading ? (
        <Card
          className="p-0 d-flex justify-content-center align-items-center m-0"
          style={{ height: 'calc(100vh - 165px)' }}>
          <CallexLoader />
        </Card>
      ) : queuesList?.data?.length > 0 &&
        checkModulePermission('is_view', 'Queues', userRolePermission) ? (
        <Card className="p-0 position-relative m-0">
          <div className="daily-report-table" style={{ height: 'calc(100vh - 210px)' }}>
            <table className="report-list-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Play Queue</th>
                  {/* <th>Queue Drop Time</th> */}
                  <th>Play Queue Frequency Time</th>
                  <th>Status</th>
                  <th className="text-center" style={{ width: '90px' }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {queuesList?.data?.map((val, index) => {
                  return (
                    <tr key={index}>
                      <td>{val?.name}</td>
                      <td>{String(val?.play_queue_no)}</td>
                      {/* <td>{val?.queue_drop_time}</td> */}
                      <td>{val?.play_queue_no_freq}</td>
                      <td>{val?.status?.toString()}</td>
                      <td>
                        <div className="d-flex justify-content-around align-items-center">
                          <div
                            className={`cursor-pointer text-info ${
                              !checkModulePermission('is_edit', 'Queues', userRolePermission)
                                ? 'callex-disabled'
                                : ''
                            }`}>
                            <Icon.Edit
                              size={16}
                              onClick={() => {
                                if (
                                  checkModulePermission('is_edit', 'Queues', userRolePermission)
                                ) {
                                  setEditRecord(val);
                                  setIsEdit(true);
                                  setOpenAddModal(true);
                                }
                              }}
                            />
                          </div>
                          <div
                            className={`cursor-pointer text-danger ${
                              !checkModulePermission('is_delete', 'Queues', userRolePermission)
                                ? 'callex-disabled'
                                : ''
                            }`}>
                            <Icon.Trash
                              size={16}
                              onClick={() => {
                                if (
                                  checkModulePermission('is_delete', 'Queues', userRolePermission)
                                ) {
                                  setIsDeleteId(val?.id);
                                }
                              }}
                            />
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="position-absolute w-100" style={{ bottom: '-56px' }}>
            <Pagination
              totalCount={queuesList?.total}
              limit={queuesList?.limit}
              page={queuesList?.page}
              onLimitChange={(val) => {
                setFilter({ ...filter, page: 1, limit: val });
              }}
              onPageClick={(val) => setFilter({ ...filter, page: val })}
              lastPageClick={(val) => setFilter({ ...filter, page: val })}
              firstPageClick={(val) => setFilter({ ...filter, page: val })}
              onRefreshClick={() => fetchQueuesList()}
            />
          </div>
        </Card>
      ) : (
        <Card
          className="p-0 d-flex justify-content-center align-items-center no-data m-0"
          style={{ height: 'calc(100vh - 165px)' }}>
          No data available
        </Card>
      )}
      <DeleteConfirmationModal
        isOpen={!!isDeleteId}
        onCloseClick={() => setIsDeleteId(null)}
        onDeleteClick={() => deleteBreak()}
      />
      <AddEditQueueModal
        isOpen={openAddModal}
        onCloseClick={(apiCall = false) => {
          setOpenAddModal(false);
          setEditRecord(null);
          setIsEdit(false);
          if (apiCall) {
            fetchQueuesList();
          }
        }}
        editRecord={editRecord}
        isEdit={isEdit}
      />
    </div>
  );
};

export default Queues;
